import isNil from "lodash/isNil";

import {
  AppointmentTypeGroup,
  ProgramName,
  ProgramNameToTreatmentTypeMapper,
  SchedulePermissionActionType,
} from "@/constants/Appointment";
import type { SchedulePermissionTreatmentType } from "@/constants/Appointment";

/**
 * Gets the treatment type from a program name
 * @param programName - The program name to convert
 * @returns The corresponding treatment type or undefined if not found
 */
const getTreatmentTypeFromProgramName = (
  programName?: string,
): SchedulePermissionTreatmentType | undefined => {
  if (isNil(programName)) return;

  const matchingKey = Object.values(ProgramName).find(
    (value) => value === programName,
  );
  if (isNil(matchingKey)) return;
  return ProgramNameToTreatmentTypeMapper[matchingKey];
};

/**
 * Gets the patient IDs that need to be processed for appointment types
 * @param externalId - The current user's external ID
 * @param isPatient - Whether the current user is a patient
 * @param allPatients - Array of all patients (for support users)
 * @returns Array of patient external IDs to process
 */
const getPatientIdsToProcess = ({
  allPatients,
  externalId,
  isPatient,
}: {
  allPatients?: LinkedPatientProfile[];
  externalId: string;
  isPatient: boolean;
}): string[] => {
  if (isPatient) {
    return externalId ? [externalId] : [];
  }
  return allPatients?.map((patient) => patient.externalId) ?? [];
};

/**
 * Determines if processing should be skipped based on current state
 * @param isPatient - Whether the current user is a patient
 * @param isCurrentUserInTreatment - Whether the current user is in treatment
 * @param hasLinkedPatientInTreatment - Whether any linked patients are in treatment
 * @returns True if processing should be skipped
 */
const shouldSkipProcessing = ({
  hasLinkedPatientInTreatment,
  isCurrentUserInTreatment,
  isPatient,
}: {
  hasLinkedPatientInTreatment: boolean;
  isCurrentUserInTreatment: boolean;
  isPatient: boolean;
}): boolean => {
  // Avoid fetching if user/patients are not in treatment
  const isPatientNotInTreatment = isPatient && !isCurrentUserInTreatment;
  const isSupportWithNotInTreatmentPatient =
    !isPatient && !hasLinkedPatientInTreatment;

  return isPatientNotInTreatment || isSupportWithNotInTreatmentPatient;
};

/**
 * Parses appointment permissions based on user type and treatment type
 * @param params - The permission parsing parameters
 * @param params.isProxy - Whether the user is a proxy
 * @param params.isSupport - Whether the user is a support user
 * @param params.permissionsDefinitions - The raw permissions from the API
 * @param params.treatmentType - The treatment type to filter by
 * @returns Parsed permissions map by appointment type group
 */
const parseAppointmentPermissions = ({
  isProxy,
  isSupport,
  permissionsDefinitions,
  treatmentType,
}: {
  isProxy: boolean;
  isSupport: boolean;
  permissionsDefinitions: SchedulePermissionDefinition[];
  treatmentType: string;
}): SchedulePermissionsMapper => {
  // Initialize all permissions to false
  const permissionsMap: SchedulePermissionsMapper = Object.fromEntries(
    Object.values(AppointmentTypeGroup).map((group) => [
      group,
      { canCancel: false },
    ]),
  );

  const isUserTypeMatches = (spd: SchedulePermissionDefinition) => {
    if (isSupport) return spd.subjectType === "SUPPORT";
    return spd.subjectType === "PATIENT";
  };

  const isSupportTypeMatches = (spd: SchedulePermissionDefinition) => {
    if (!isSupport || spd.subjectId === "*") return true;
    if (isProxy) return spd.subjectId === "PROXY";
    return spd.subjectId === "GENERAL";
  };

  const isTreatmentTypeMatches = (spd: SchedulePermissionDefinition) => {
    return spd.targetSubTypeId === treatmentType;
  };

  const filteredPermissions = permissionsDefinitions
    .filter(isUserTypeMatches)
    .filter(isSupportTypeMatches)
    .filter(isTreatmentTypeMatches);

  // eslint-disable-next-line max-params
  const updatedPermissionsMap = filteredPermissions.reduce((acc, current) => {
    const currentPermission = acc[current.targetId] ?? { canCancel: false };

    if (current.actionType === SchedulePermissionActionType.Cancel) {
      currentPermission.canCancel = true;
    }

    return {
      ...acc,
      [current.targetId]: currentPermission,
    };
  }, permissionsMap);

  return updatedPermissionsMap;
};

export {
  getPatientIdsToProcess,
  getTreatmentTypeFromProgramName,
  parseAppointmentPermissions,
  shouldSkipProcessing,
};
